import type { ReactNode } from 'react';
import { ECRAN_LARGE, useRequeteMedia } from '../ui';
import { FeuilleFormulaire } from './FeuilleFormulaire';
import { ModesAcces } from './ModesAcces';
import { DetailsPresentation, PresentationRepere } from './Presentation';

/**
 * Illustration des écrans d'entrée, sur grand écran seulement.
 *
 * Elle occupait aussi un bandeau sur téléphone — c'est ce que racontait ce commentaire
 * avant que ce ne soit plus le cas. La photo n'apporte rien que le texte ne dise déjà
 * (`alt=""` le dit depuis toujours), et sur un écran de 360 px elle coûtait 170 px de
 * hauteur avant même d'atteindre le bouton qui ouvre le formulaire. Elle reste dans la
 * colonne de gauche du grand écran, où la place ne se dispute avec rien.
 *
 * Dimensions intrinsèques renseignées : sans elles, la place n'est réservée qu'une fois
 * l'image arrivée, et la colonne saute pendant le chargement. `fetchPriority="low"` la
 * laisse passer après le formulaire, qui est ce que le parent vient faire.
 */
function IllustrationEntree() {
  return (
    <img
      className="entree-illustration"
      // Chemin absolu INTERDIT ici : l'application est servie sous `/app`, donc
      // `/illustration-entree.webp` designe la racine — la vitrine — ou ce
      // fichier n'existe pas. Les ressources de `public/` se referencent par
      // BASE_URL, qui vaut `/app/` apres compilation et `/` en developpement.
      src={`${import.meta.env.BASE_URL}illustration-entree.webp`}
      alt=""
      width={1024}
      height={572}
      decoding="async"
      fetchPriority="low"
    />
  );
}

/**
 * Mise en page des écrans d'entrée — connexion et inscription.
 *
 * Deux colonnes sur grand écran : ce que fait Repère à gauche, ce qu'on demande à droite.
 * Un formulaire de deux champs étiré sur toute la largeur d'un écran de bureau se lit mal
 * et donne au produit un air de page inachevée.
 *
 * En dessous, l'ordre devient : titre, explication, bandeau illustré, puis un bouton
 * d'appel. Le formulaire lui-même monte du bas de l'écran à la demande — sur un téléphone,
 * ce qui compte tient sous le pouce, et une paire de champs posée en haut de page oblige
 * à remonter pour la remplir.
 */
export function EcranEntree({
  titre,
  alternative,
  children,
  variante = 'inscription',
}: {
  titre: string;
  /** L'autre porte d'entrée : s'inscrire depuis la connexion, et réciproquement. */
  alternative?: ReactNode;
  children: ReactNode;
  /**
   * `connexion` allège l'écran : une phrase de présentation au lieu de trois, pas de
   * section « invitation reçue », et le formulaire déjà ouvert sur téléphone.
   *
   * Ce n'est pas une variante cosmétique — les deux écrans servent deux personnes. Celle
   * qui s'inscrit a besoin qu'on lui explique ; celle qui se connecte a besoin d'entrer.
   */
  variante?: 'connexion' | 'inscription';
}) {
  const ecranLarge = useRequeteMedia(ECRAN_LARGE);

  /* Hors de la feuille : quelqu'un qui n'a pas encore de compte doit voir par où passer
     sans avoir à ouvrir un formulaire de connexion pour s'en apercevoir. */
  const porte = alternative ? <div className="entree-alternative">{alternative}</div> : null;

  /**
   * Sur téléphone, l'accroche courte remplace la longue — y compris à l'inscription.
   *
   * Elle était réservée à la connexion : qui revient a déjà lu l'argumentaire, le lui
   * reservir coûte une lecture à chaque visite. Mais sur un écran de 360 px, les trois
   * paragraphes de la version longue plus l'illustration plaçaient le bouton qui ouvre le
   * formulaire à deux écrans et demi de défilement — avant même de savoir qu'il existait.
   * Ce que la version longue dit en plus (`DetailsPresentation`) n'est pas perdu : sur
   * mobile, il rejoint la feuille du formulaire, au moment où l'on s'apprête vraiment à
   * créer un compte. Sur grand écran, rien ne change : la colonne a la place pour tout.
   */
  const presentationCourte = variante === 'connexion' || !ecranLarge;

  return (
    <div className="entree">
      <div className="entree-presentation">
        <h1>{titre}</h1>
        <PresentationRepere court={presentationCourte} />
        {ecranLarge ? <IllustrationEntree /> : null}
      </div>

      {/* Deux structures, pas deux formulaires : le même contenu est simplement parenté
          différemment. Le dupliquer aurait fait exister deux champs de mot de passe dans
          le document, et les remplissages automatiques s'y perdent. */}
      {ecranLarge ? (
        /* L'autre porte d'entrée tient dans la colonne du formulaire, pas dans une rangée
           à elle : rangée voisine, elle héritait de l'écart entre colonnes — 48 px, plus
           la marge de la carte — et son bouton se retrouvait seul au milieu du vide. */
        <div className="entree-formulaire">
          {children}
          {porte}
        </div>
      ) : (
        <>
          <FeuilleFormulaire libelle={titre} ouverteDEmblee={variante === 'connexion'}>
            {children}
            {/* Ce que la présentation courte a laissé de côté, et « est-ce que ça va
                m'engager ? » : les deux ne se posent qu'à l'inscription, et seulement
                une fois qu'on est vraiment en train d'en créer une — pas avant d'avoir
                trouvé le bouton qui ouvre cette feuille. */}
            {variante === 'inscription' ? (
              <>
                <DetailsPresentation />
                <ModesAcces />
              </>
            ) : null}
          </FeuilleFormulaire>
          {porte}
        </>
      )}

      {/* Seulement sur grand écran désormais : sur téléphone, la question de
          l'engagement a rejoint la feuille du formulaire (ci-dessus), au plus près du
          moment où elle se pose vraiment.
          Sur toute la largeur, sous les deux colonnes : la question de l'engagement se
          pose une fois, pas une par colonne. En pile, elle vient après le formulaire —
          celui qui a déjà un compte n'a pas à la traverser pour se connecter. */}
      {ecranLarge && variante === 'inscription' ? <ModesAcces /> : null}
    </div>
  );
}
